/**
 * Integrações › **Armazenamento** (D727) - the object store's real bucket (Cloudflare R2): the account id, the bucket, the R2 API token's access key pair
 * (WRITE-ONLY: sealed in the vault; the screen shows only the access key id's last characters) and the switch, plus a connection test that writes, reads back
 * and deletes a probe object. The badge says which backend is in use now - the bucket, or the temporary memory (files lost on a restart) - and whether the
 * deployment mounts the file encryption key without which the bucket is never used. Composes `@vello/web-theme` only; the rules are `normalizeStorageForm`.
 */
import { type FormEvent, type ReactElement, useEffect, useId, useState } from 'react';
import type { StorageIntegrationView } from '@vello/api-client';
import { Badge, Button, Cluster, Field, FormGrid, Notice, Panel, Select, Stack, Text, TextInput } from '@vello/web-theme';
import {
  type StorageFormField,
  type StorageFormIssue,
  type StorageIntegrationForm,
  emptyStorageForm,
  getStorageIntegration,
  normalizeStorageForm,
  setStorageIntegration,
  storageFormFromView,
  testStorageIntegration,
} from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

const ISSUE_KEY: Readonly<Record<StorageFormIssue['code'], string>> = {
  INVALID_ACCOUNT_ID: 'int.storage.err.accountId',
  INVALID_BUCKET: 'int.storage.err.bucket',
  KEY_PAIR_INCOMPLETE: 'int.storage.err.pair',
  KEY_PAIR_REQUIRED: 'int.storage.err.pairRequired',
};

export function StoragePanel(): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { fail } = useVocab();
  const id = useId();
  const [view, setView] = useState<StorageIntegrationView | undefined>(undefined);
  const [form, setForm] = useState<StorageIntegrationForm>(() => emptyStorageForm());
  const [errors, setErrors] = useState<Partial<Record<StorageFormField, string>>>({});
  const [msg, setMsg] = useState<{ readonly tone: 'success' | 'danger'; readonly text: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [testMsg, setTestMsg] = useState<{ readonly tone: 'success' | 'danger'; readonly text: string } | undefined>(undefined);
  const [testBusy, setTestBusy] = useState(false);
  const set = (field: keyof StorageIntegrationForm) => (v: string | boolean): void => setForm((f) => ({ ...f, [field]: v }));

  useEffect(() => {
    let cancelled = false;
    void getStorageIntegration(runtime).then((res) => {
      if (cancelled) return;
      if (res.ok) {
        setView(res.value);
        setForm(storageFormFromView(res.value));
      } else setMsg({ tone: 'danger', text: t('int.load.failed', { code: fail(res.error) }) });
    });
    return () => {
      cancelled = true;
    };
  }, [runtime, t]);

  const configured = view?.configured === true;

  const onSave = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setMsg(undefined);
    const normalized = normalizeStorageForm(form, configured);
    if (!normalized.ok) {
      const next: Partial<Record<StorageFormField, string>> = {};
      for (const issue of normalized.error) next[issue.field] = t(ISSUE_KEY[issue.code]);
      setErrors(next);
      return;
    }
    setErrors({});
    setBusy(true);
    const res = await setStorageIntegration(runtime, normalized.value);
    setBusy(false);
    if (res.ok) {
      setView(res.value);
      setForm(storageFormFromView(res.value));
      setMsg({ tone: 'success', text: t('int.saved') });
    } else setMsg({ tone: 'danger', text: t('int.save.failed', { code: fail(res.error) }) });
  };

  const onTest = async (): Promise<void> => {
    setTestMsg(undefined);
    setTestBusy(true);
    const res = await testStorageIntegration(runtime);
    setTestBusy(false);
    if (res.ok) {
      setTestMsg({ tone: 'success', text: t('int.storage.test.ok', { bucket: res.value.bucket, ms: res.value.elapsedMs }) });
      return;
    }
    const reason = res.error.reasonCode;
    const text = reason === 'STORAGE_NOT_CONFIGURED' ? t('int.storage.test.notConfigured') : reason === 'STORAGE_ENCRYPTION_KEY_MISSING' ? t('int.storage.noEncryptionKey') : t('int.storage.test.failed', { code: fail(res.error) });
    setTestMsg({ tone: 'danger', text });
  };

  const backend = view?.backend ?? 'sandbox';
  return (
    <Panel>
      <Stack gap={4}>
        <Cluster gap={3} align="baseline" wrap>
          <Text as="h2" role="title">{t('int.storage.heading')}</Text>
          <Badge tone={configured ? 'success' : 'warning'}>{configured ? t('int.status.configured') : t('int.status.notConfigured')}</Badge>
          <Badge tone={backend === 'r2' ? 'success' : 'neutral'}>{backend === 'r2' ? t('int.storage.backend.r2') : t('int.storage.backend.sandbox')}</Badge>
        </Cluster>
        <Text as="p" tone="secondary">{t('int.storage.hint')}</Text>
        {view !== undefined && backend === 'sandbox' && <Text as="p" tone="secondary">{t('int.storage.sandbox.hint')}</Text>}
        {view !== undefined && !view.encryptionKey && <Notice tone="warning">{t('int.storage.noEncryptionKey')}</Notice>}
        <form onSubmit={(e) => void onSave(e)} noValidate aria-label={t('int.storage.heading')}>
          <Stack gap={4}>
            <FormGrid>
              <Field label={t('int.storage.field.accountId')} htmlFor={`${id}-account`} hint={errors.accountId ?? t('int.storage.field.accountId.hint')}>
                <TextInput id={`${id}-account`} value={form.accountId} onChange={(e) => set('accountId')(e.target.value)} spellCheck={false} autoComplete="off" />
              </Field>
              <Field label={t('int.storage.field.bucket')} htmlFor={`${id}-bucket`} hint={errors.bucket ?? t('int.storage.field.bucket.hint')}>
                <TextInput id={`${id}-bucket`} value={form.bucket} onChange={(e) => set('bucket')(e.target.value)} spellCheck={false} autoComplete="off" placeholder="vello-staging" />
              </Field>
              <Field label={t('int.storage.field.accessKeyId')} htmlFor={`${id}-akid`} hint={errors.keyPair ?? (configured ? t('int.storage.field.keys.hintConfigured', { hint: view?.accessKeyIdHint ?? '' }) : t('int.storage.field.keys.hint'))}>
                <TextInput id={`${id}-akid`} type="password" autoComplete="off" value={form.accessKeyId} onChange={(e) => set('accessKeyId')(e.target.value)} placeholder={configured ? '••••••••' : ''} />
              </Field>
              <Field label={t('int.storage.field.secretAccessKey')} htmlFor={`${id}-sak`}>
                <TextInput id={`${id}-sak`} type="password" autoComplete="off" value={form.secretAccessKey} onChange={(e) => set('secretAccessKey')(e.target.value)} placeholder={configured ? '••••••••' : ''} />
              </Field>
              <Field label={t('int.field.enabled')} htmlFor={`${id}-enabled`} hint={t('int.storage.field.enabled.hint')}>
                <Select id={`${id}-enabled`} value={form.enabled ? 'on' : 'off'} onChange={(e) => set('enabled')(e.target.value === 'on')}>
                  <option value="on">{t('int.field.enabled.on')}</option>
                  <option value="off">{t('int.field.enabled.off')}</option>
                </Select>
              </Field>
            </FormGrid>
            {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
            <div>
              <Button type="submit" variant="secondary" disabled={busy}>{busy ? t('int.save.busy') : t('int.save')}</Button>
            </div>
          </Stack>
        </form>
        <div>
          <Text as="h3" role="label">{t('int.storage.test.heading')}</Text>
          <Text as="p" tone="secondary">{t('int.storage.test.hint')}</Text>
          <Button variant="secondary" disabled={testBusy || !configured} onClick={() => void onTest()}>{testBusy ? t('int.storage.test.busy') : t('int.storage.test.run')}</Button>
          {testMsg !== undefined && <Notice tone={testMsg.tone}>{testMsg.text}</Notice>}
        </div>
      </Stack>
    </Panel>
  );
}
